import { useEffect, useRef } from 'react';
import {
  Loader2,
  Maximize2,
  Mic,
  MicOff,
  Minimize2,
  PhoneOff,
  RefreshCw,
  Repeat2,
  SwitchCamera,
  Video,
  VideoOff,
} from 'lucide-react';
import type {
  CallStatus,
  ConnectionPhase,
  FacingMode,
  VideoFit,
} from '../../context/VideoCallContext';
import type { PublicUser } from '../../types';

interface ActiveCallViewProps {
  status: CallStatus;
  connectionPhase: ConnectionPhase;
  remoteUser: PublicUser;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  muted: boolean;
  cameraOff: boolean;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  onEndCall: () => void;
  facingMode: FacingMode;
  canSwitchCamera: boolean;
  switchingCamera: boolean;
  onSwitchCamera: () => void;
  zoomPresets: number[];
  activeZoom: number;
  onZoom: (preset: number) => void;
  swapped: boolean;
  onToggleSwap: () => void;
  videoFit: VideoFit;
  onToggleFit: () => void;
}

function formatZoom(z: number): string {
  // 0.5 -> "0.5", 1 -> "1", 2 -> "2"
  return `${Number.isInteger(z) ? z : z.toFixed(1)}×`;
}

export default function ActiveCallView({
  status,
  connectionPhase,
  remoteUser,
  localStream,
  remoteStream,
  muted,
  cameraOff,
  onToggleMute,
  onToggleCamera,
  onEndCall,
  facingMode,
  canSwitchCamera,
  switchingCamera,
  onSwitchCamera,
  zoomPresets,
  activeZoom,
  onZoom,
  swapped,
  onToggleSwap,
  videoFit,
  onToggleFit,
}: ActiveCallViewProps) {
  const mainVideoRef = useRef<HTMLVideoElement | null>(null);
  const pipVideoRef = useRef<HTMLVideoElement | null>(null);

  // By default the remote peer fills the screen and you're the small tile.
  // When swapped, your camera becomes the big view and the peer is the tile.
  const mainStream = swapped ? localStream : remoteStream;
  const pipStream = swapped ? remoteStream : localStream;
  const isMainLocal = swapped;
  const isPipLocal = !swapped;

  useEffect(() => {
    if (mainVideoRef.current && mainStream) {
      mainVideoRef.current.srcObject = mainStream;
      void mainVideoRef.current.play().catch(() => {});
    }
  }, [mainStream]);

  useEffect(() => {
    if (pipVideoRef.current && pipStream) {
      pipVideoRef.current.srcObject = pipStream;
      void pipVideoRef.current.play().catch(() => {});
    }
  }, [pipStream]);

  const displayName = remoteUser.name || remoteUser.email || 'Friend';
  const fitClass = videoFit === 'cover' ? 'object-cover' : 'object-contain';
  // Only mirror the local front-facing camera; remote + rear cameras stay as-is.
  const localMirror = facingMode === 'user' ? 'mirror' : '';
  const mainHasVideo = isMainLocal ? !cameraOff : !!remoteStream;
  // Zoom controls apply to the local camera; only meaningful when it's running.
  const showZoom = zoomPresets.length > 1 && !cameraOff;

  return (
    <div className="fixed inset-0 z-[100] h-[100dvh] bg-gray-900 flex flex-col min-h-0">
      {/* Main video (full screen) */}
      <div className="relative flex-1 min-h-0 overflow-hidden bg-black">
        {mainStream && mainHasVideo ? (
          <video
            ref={mainVideoRef}
            autoPlay
            playsInline
            muted={isMainLocal}
            className={`absolute inset-0 block w-full h-full ${fitClass} ${
              isMainLocal ? localMirror : ''
            }`}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80 gap-3">
            {isMainLocal && cameraOff ? (
              <>
                <VideoOff className="h-10 w-10" />
                <p>Your camera is off</p>
              </>
            ) : status === 'outgoing' ? (
              <>
                <Loader2 className="h-10 w-10 animate-spin" />
                <p>Calling {displayName}…</p>
              </>
            ) : (
              <p>Connecting…</p>
            )}
          </div>
        )}

        {/* Picture-in-picture — tap to swap which feed is large */}
        <button
          type="button"
          onClick={onToggleSwap}
          className="group absolute top-4 right-4 z-10 w-28 h-[4.5rem] sm:w-40 sm:h-[7.5rem] rounded-xl overflow-hidden border-2 border-white/30 shadow-lg bg-gray-800"
          aria-label="Swap main and small video"
        >
          {isPipLocal && cameraOff ? (
            <div className="absolute inset-0 flex items-center justify-center text-white/60">
              <VideoOff className="h-8 w-8" />
            </div>
          ) : (
            <video
              ref={pipVideoRef}
              autoPlay
              playsInline
              muted={isPipLocal}
              className={`absolute inset-0 block w-full h-full object-cover ${
                isPipLocal ? localMirror : ''
              }`}
            />
          )}
          <span className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-1">
            <Repeat2 className="h-3.5 w-3.5 text-white" />
          </span>
        </button>

        <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm font-medium">
          {displayName}
        </div>

        {/* Reconnecting banner — shown when the link drops on a poor connection */}
        {status === 'active' && connectionPhase === 'reconnecting' && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/90 text-white text-sm font-medium shadow-lg">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Reconnecting…
          </div>
        )}

        {/* Zoom presets — small transparent pills (0.5× / 1× / 2× / 3×) */}
        {showZoom && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-4 z-10 flex items-center gap-1.5 px-1.5 py-1.5 rounded-full bg-black/35 backdrop-blur-sm">
            {zoomPresets.map((preset) => {
              const isActive = Math.abs(activeZoom - preset) < 0.05;
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onZoom(preset)}
                  disabled={switchingCamera}
                  className={`min-w-[2.75rem] h-9 px-2 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 ${
                    isActive
                      ? 'bg-white text-gray-900 shadow'
                      : 'bg-white/10 text-white/90 hover:bg-white/20'
                  }`}
                  aria-label={`Zoom ${formatZoom(preset)}`}
                  aria-pressed={isActive}
                >
                  {formatZoom(preset)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3 sm:gap-5 py-6 px-4 bg-gray-900/95 border-t border-white/10">
        <button
          type="button"
          onClick={onToggleMute}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            muted ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'
          }`}
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>

        <button
          type="button"
          onClick={onToggleCamera}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            cameraOff ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'
          }`}
          aria-label={cameraOff ? 'Turn camera on' : 'Turn camera off'}
        >
          {cameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
        </button>

        {canSwitchCamera && (
          <button
            type="button"
            onClick={onSwitchCamera}
            disabled={switchingCamera || cameraOff}
            className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Switch camera"
            title={facingMode === 'user' ? 'Switch to rear camera' : 'Switch to front camera'}
          >
            {switchingCamera ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <SwitchCamera className="h-5 w-5" />
            )}
          </button>
        )}

        <button
          type="button"
          onClick={onEndCall}
          className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg"
          aria-label="End call"
        >
          <PhoneOff className="h-6 w-6" />
        </button>

        <button
          type="button"
          onClick={onToggleSwap}
          className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-700 text-white hover:bg-gray-600"
          aria-label="Swap main and small video"
          title="Swap big / small view"
        >
          <Repeat2 className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={onToggleFit}
          className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-700 text-white hover:bg-gray-600"
          aria-label={videoFit === 'cover' ? 'Fit video (show whole frame)' : 'Fill screen'}
          title={videoFit === 'cover' ? 'Fit whole frame' : 'Fill screen'}
        >
          {videoFit === 'cover' ? (
            <Minimize2 className="h-5 w-5" />
          ) : (
            <Maximize2 className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
}
